'use client';

import { DocumentAttachmentProps } from '@/@studio-ai/props';
import {
    Button,
    Card,
    CardBody,
    Chip,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ScrollShadow,
    useDisclosure,
} from '@heroui/react';
import { Icon } from '@iconify/react';

/**
 * Get the appropriate icon based on file type
 */
function getFileIcon(fileName: string, contentType?: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase();

    // Spreadsheet types
    if (
        ext === 'xlsx' ||
        ext === 'xls' ||
        ext === 'csv' ||
        contentType?.includes('spreadsheet') ||
        contentType?.includes('excel')
    ) {
        return 'lucide:file-spreadsheet';
    }

    // PDF
    if (ext === 'pdf' || contentType?.includes('pdf')) {
        return 'lucide:file-text';
    }

    // Word documents
    if (ext === 'docx' || ext === 'doc' || contentType?.includes('document')) {
        return 'lucide:file-text';
    }

    // Text files
    if (ext === 'txt' || contentType?.includes('text')) {
        return 'lucide:file-type';
    }

    // JSON
    if (ext === 'json' || contentType?.includes('json')) {
        return 'lucide:file-json';
    }

    // Default
    return 'lucide:file';
}

/**
 * Format file size to human-readable format
 */
function formatFileSize(bytes?: number): string {
    if (!bytes) return '';

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
}

type FileTypeColor = 'success' | 'danger' | 'primary' | 'warning' | 'default';

/**
 * Static class mappings for file type colors
 * Using explicit classes instead of dynamic construction for Tailwind purge compatibility
 */
const fileTypeColorClasses: Record<FileTypeColor, { bg: string; text: string }> = {
    success: { bg: 'bg-success/10', text: 'text-success' },
    danger: { bg: 'bg-danger/10', text: 'text-danger' },
    primary: { bg: 'bg-primary/10', text: 'text-primary' },
    warning: { bg: 'bg-warning/10', text: 'text-warning' },
    default: { bg: 'bg-default/10', text: 'text-default-500' },
};

/**
 * Get a color class based on file type
 */
function getFileTypeColor(fileName: string): FileTypeColor {
    const ext = fileName.split('.').pop()?.toLowerCase();

    switch (ext) {
        case 'xlsx':
        case 'xls':
        case 'csv':
            return 'success';
        case 'pdf':
            return 'danger';
        case 'docx':
        case 'doc':
            return 'primary';
        case 'json':
            return 'warning';
        default:
            return 'default';
    }
}

export function DocumentAttachment({
    fileName,
    fileSize,
    contentType,
    className = '',
    extractedText,
    extractionMethod,
}: DocumentAttachmentProps) {
    const { isOpen, onOpen, onOpenChange } = useDisclosure();
    const icon = getFileIcon(fileName, contentType);
    const color = getFileTypeColor(fileName);
    const colorClasses = fileTypeColorClasses[color];
    const ext = fileName.split('.').pop()?.toUpperCase();

    // Check if we have text to show
    const hasExtractedText = extractedText && extractedText.trim().length > 0;

    return (
        <>
            <Card
                className={`w-fit max-w-xs self-end border border-default-200 bg-default-50 shadow-sm transition-all ${
                    hasExtractedText
                        ? 'cursor-pointer hover:border-primary-300 hover:bg-primary-50/30 hover:shadow-md'
                        : ''
                } ${className}`}
                radius="lg"
                isPressable={Boolean(hasExtractedText)}
                onPress={hasExtractedText ? onOpen : undefined}
            >
                <CardBody className="flex flex-row items-center gap-3 p-3">
                    <div
                        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${colorClasses.bg}`}
                    >
                        <Icon icon={icon} width={24} height={24} className={colorClasses.text} />
                    </div>
                    <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="truncate text-small font-medium text-foreground">
                            {fileName}
                        </span>
                        <div className="flex items-center gap-2">
                            {ext && (
                                <Chip
                                    size="sm"
                                    variant="flat"
                                    color={
                                        color as
                                            | 'default'
                                            | 'primary'
                                            | 'secondary'
                                            | 'success'
                                            | 'warning'
                                            | 'danger'
                                    }
                                    className="h-5 text-tiny"
                                >
                                    {ext}
                                </Chip>
                            )}
                            {fileSize && (
                                <span className="text-tiny text-default-400">
                                    {formatFileSize(fileSize)}
                                </span>
                            )}
                            {hasExtractedText && (
                                <Icon
                                    icon="lucide:eye"
                                    width={12}
                                    height={12}
                                    className="text-default-400"
                                />
                            )}
                        </div>
                    </div>
                </CardBody>
            </Card>

            {/* Modal to display extracted text */}
            <Modal
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                size="4xl"
                scrollBehavior="inside"
                classNames={{
                    base: 'max-h-[85vh]',
                    header: 'border-b border-divider',
                    footer: 'border-t border-divider',
                    body: 'p-0',
                }}
            >
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">
                                <div className="flex items-center gap-3">
                                    <div
                                        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${colorClasses.bg}`}
                                    >
                                        <Icon
                                            icon={icon}
                                            width={18}
                                            height={18}
                                            className={colorClasses.text}
                                        />
                                    </div>
                                    <div>
                                        <h3 className="text-large font-semibold">{fileName}</h3>
                                        <div className="flex items-center gap-2 text-small text-default-500">
                                            {ext && <span>{ext} Document</span>}
                                            {fileSize && (
                                                <>
                                                    <span>•</span>
                                                    <span>{formatFileSize(fileSize)}</span>
                                                </>
                                            )}
                                            {extractionMethod && (
                                                <>
                                                    <span>•</span>
                                                    <span className="capitalize">
                                                        Extracted via {extractionMethod}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </ModalHeader>
                            <ModalBody>
                                <ScrollShadow className="max-h-[60vh]">
                                    <pre className="whitespace-pre-wrap break-words bg-default-50 p-4 font-mono text-small text-default-700">
                                        {extractedText}
                                    </pre>
                                </ScrollShadow>
                            </ModalBody>
                            <ModalFooter>
                                <Button
                                    variant="flat"
                                    onPress={() => {
                                        if (extractedText) {
                                            navigator.clipboard.writeText(extractedText);
                                        }
                                    }}
                                    startContent={
                                        <Icon icon="lucide:copy" width={16} height={16} />
                                    }
                                >
                                    Copy Text
                                </Button>
                                <Button color="primary" onPress={onClose}>
                                    Close
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </>
    );
}
